import { useState, useEffect, useSyncExternalStore } from "react";

/* ------------------------------------------------------------------ *
 * client/src/i18n.jsx — React layer of the hash-based i18n (team-i18n).
 *
 * Strings are keyed by SHA-256(trimmed English).slice(0,16) — byte-for-byte identical to the
 * server's i18nHash (verified). Components render their English source inside a
 * <span data-i18n="<hash>">…</span>; the visible text is the current language's translation
 * (falling back to English), so QA can spot-check that data-i18n matches the source while the
 * UI shows the right language. The vanilla /js/* scripts handle any non-React DOM and the
 * <html lang/dir> wiring; this module is the in-app path.
 * ------------------------------------------------------------------ */

/* ---- sync SHA-256 (geraintluff, MIT) — matches server util/hash.js i18nHash ---- */
function sha256(ascii) {
  function rightRotate(value, amount) { return (value >>> amount) | (value << (32 - amount)); }
  var mathPow = Math.pow, maxWord = mathPow(2, 32), lengthProperty = "length", i, j, result = "";
  var words = [], asciiBitLength = ascii[lengthProperty] * 8;
  var hash = sha256.h = sha256.h || [], k = sha256.k = sha256.k || [], primeCounter = k[lengthProperty];
  var isComposite = {};
  for (var candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (i = 0; i < 313; i += candidate) isComposite[i] = candidate;
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }
  ascii += "\x80";
  while (ascii[lengthProperty] % 64 - 56) ascii += "\x00";
  for (i = 0; i < ascii[lengthProperty]; i++) {
    j = ascii.charCodeAt(i);
    if (j >> 8) return;
    words[i >> 2] |= j << ((3 - i) % 4) * 8;
  }
  words[words[lengthProperty]] = ((asciiBitLength / maxWord) | 0);
  words[words[lengthProperty]] = (asciiBitLength);
  for (j = 0; j < words[lengthProperty];) {
    var w = words.slice(j, j += 16), oldHash = hash;
    hash = hash.slice(0, 8);
    for (i = 0; i < 64; i++) {
      var w15 = w[i - 15], w2 = w[i - 2];
      var a = hash[0], e = hash[4];
      var temp1 = hash[7] + (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) + ((e & hash[5]) ^ ((~e) & hash[6])) + k[i]
        + (w[i] = i < 16 ? w[i] : (w[i - 16] + (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) + w[i - 7] + (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) | 0);
      var temp2 = (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) + ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));
      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
    }
    for (i = 0; i < 8; i++) hash[i] = (hash[i] + oldHash[i]) | 0;
  }
  for (i = 0; i < 8; i++) for (j = 3; j + 1; j--) { var b = (hash[i] >> (j * 8)) & 255; result += ((b < 16) ? 0 : "") + b.toString(16); }
  return result;
}
const utf8 = (s) => unescape(encodeURIComponent(String(s)));
const _hcache = new Map();
export function i18nHash(text) {
  const key = String(text).trim();
  let h = _hcache.get(key);
  if (!h) { h = sha256(utf8(key)).slice(0, 16); _hcache.set(key, h); }
  return h;
}

/* ---- language + strings state ---- */
const RTL = ["ar", "he", "fa", "ur"];
const detect = () => {
  if (typeof window !== "undefined" && window.__i18n && window.__i18n.lang) return window.__i18n.lang;
  try {
    const u = new URLSearchParams(location.search);
    const q = u.get("lang") || u.get("lng") || u.get("language");
    if (q) return q.toLowerCase().split("-")[0];
    const c = document.cookie.match(/(?:^|;\s*)preferredLanguage=([^;]+)/);
    if (c) return decodeURIComponent(c[1]).toLowerCase().split("-")[0];
    const ls = localStorage.getItem("preferredLanguage");
    if (ls) return ls.toLowerCase().split("-")[0];
    if (navigator.language) return navigator.language.toLowerCase().split("-")[0];
  } catch { /* ignore */ }
  return "en";
};

let _lang = "en";
let _strings = {};            // hash -> translated text
const _listeners = new Set();
const _pending = new Set();   // unknown English queued for registration
let _flush = null;

function notify() { _listeners.forEach((fn) => fn()); }

async function fetchStrings(lang) {
  try {
    const res = await fetch(`/api/i18n/strings?lang=${encodeURIComponent(lang)}`);
    if (!res.ok) return { lang, strings: {} };
    return await res.json();
  } catch { return { lang, strings: {} }; }
}

/** Load (or reload) strings for a language and apply <html lang/dir>. Returns when ready. */
export async function loadLanguage(lang) {
  const l = String(lang || "en").toLowerCase().split("-")[0];
  const data = await fetchStrings(l);
  _lang = data.lang || l;
  _strings = data.strings || {};
  try {
    document.documentElement.lang = _lang;
    document.documentElement.dir = RTL.includes(_lang) ? "rtl" : "ltr";
  } catch { /* ignore */ }
  notify();
  return _lang;
}

/** Called once from main.jsx before render. */
export async function initI18n() {
  return loadLanguage(detect());
}

/** Persist + switch language (coordinates with the vanilla scripts if present). */
export async function setLanguage(lang) {
  const l = String(lang || "en").toLowerCase().split("-")[0];
  try {
    if (window.setLanguage) { window.setLanguage(l); }      // vanilla persists + dispatches
    else {
      document.cookie = `preferredLanguage=${l};path=/;max-age=${60 * 60 * 24 * 30}`;
      localStorage.setItem("preferredLanguage", l);
    }
  } catch { /* ignore */ }
  await loadLanguage(l);
  try { window.dispatchEvent(new CustomEvent("i18n:changed", { detail: { lang: l } })); } catch { /* ignore */ }
}

// Keep React in sync if the vanilla translator / another tab changes the language.
if (typeof window !== "undefined") {
  window.addEventListener("i18n:changed", (e) => {
    const l = e && e.detail && e.detail.lang;
    if (l && l !== _lang) loadLanguage(l);
  });
}

/* ------------------------------------------------------------------ *
 * Contextual-help widget bridge (server/runtime/public/contextual-help.js)
 *
 * That vendored "?" widget is a standalone vanilla IIFE. By design it integrates with
 * the host app through a global `window.I18n = { lang, t, applyTo }`:
 *   - lang     → the code it sends as the `x-app-lang` header on /api/help/ask, so the
 *                LLM gateway answers in the user's language (incl. Classical Chinese for zh).
 *   - t(src)   → translate one of its own dynamic UI strings (English source in).
 *   - applyTo(el) → translate the [data-i18n] / [data-i18n-attr] nodes it injects.
 * Without this global it falls back to English for BOTH its UI and the LLM reply.
 *
 * The app never published `window.I18n` (it uses `<T>`/hash-keyed `data-i18n="<hash>"`),
 * so we adapt here. `applyTo` honours the widget's convention: an EMPTY `data-i18n`
 * means "translate my own text" — which also cleanly distinguishes it from the app's
 * hash-valued nodes, so we never touch those.
 */
if (typeof window !== "undefined") {
  const applyTo = (el) => {
    if (!el || el.nodeType !== 1) return;
    const nodes = [];
    if (el.hasAttribute("data-i18n") || el.hasAttribute("data-i18n-attr")) nodes.push(el);
    if (el.querySelectorAll) el.querySelectorAll("[data-i18n], [data-i18n-attr]").forEach((n) => nodes.push(n));
    for (const n of nodes) {
      // Text: widget marks nodes with an EMPTY data-i18n; the app's own nodes carry a hash value.
      if (n.hasAttribute("data-i18n") && !n.getAttribute("data-i18n")) {
        if (n.__cxSrc == null) n.__cxSrc = (n.textContent || "").trim();   // remember the English source
        if (n.__cxSrc) { const tr = t(n.__cxSrc); if (tr && n.textContent !== tr) n.textContent = tr; }
      }
      // Attribute (placeholder / title): data-i18n-attr names the attribute to translate.
      const attr = n.getAttribute && n.getAttribute("data-i18n-attr");
      if (attr) {
        if (n.__cxAttrSrc == null) n.__cxAttrSrc = n.getAttribute(attr) || "";
        if (n.__cxAttrSrc) { const tr = t(n.__cxAttrSrc); if (tr && n.getAttribute(attr) !== tr) n.setAttribute(attr, tr); }
      }
    }
  };
  window.I18n = window.I18n || {};
  Object.defineProperty(window.I18n, "lang", { get: () => _lang, configurable: true });
  window.I18n.t = (src) => t(src);
  window.I18n.applyTo = applyTo;
  // The widget re-translates itself on `i18n:languagechange`; the app emits `i18n:changed`.
  window.addEventListener("i18n:changed", () => {
    try { window.dispatchEvent(new CustomEvent("i18n:languagechange")); } catch { /* ignore */ }
  });
}

/* ---- registration of newly-seen English (batched, idempotent) ---- */
function queueRegister(en) {
  _pending.add(en);
  if (_flush) return;
  _flush = setTimeout(async () => {
    const texts = [..._pending]; _pending.clear(); _flush = null;
    if (!texts.length) return;
    try {
      await fetch("/api/i18n/register", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texts }),
      });
    } catch { /* best-effort */ }
  }, 800);
}

/** Translate an English source string for the current language (sync). */
export function t(en, vars) {
  const src = String(en);
  const h = i18nHash(src);
  let out = Object.prototype.hasOwnProperty.call(_strings, h) ? _strings[h] : null;
  if (out == null) { out = src; queueRegister(src.trim()); } // register so translators see it
  if (vars) out = out.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
  return out;
}

/* ---- React bindings ---- */
const subscribe = (fn) => { _listeners.add(fn); return () => _listeners.delete(fn); };
const getSnapshot = () => _lang;

/** Re-renders its subtree when the language changes; exposes { lang, dir, t }. */
export function useLang() {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { lang, dir: RTL.includes(lang) ? "rtl" : "ltr", t };
}

/**
 * <T>English source</T>  →  <span data-i18n="hash">translated</span>
 * - children MUST be a static English string (optionally with {placeholders})
 * - `vars` substitutes placeholders; `comp` swaps the wrapper element (e.g. "div")
 */
export function T({ children, vars, comp: Comp = "span", ...rest }) {
  useLang(); // subscribe so this node re-renders on language change
  const en = Array.isArray(children) ? children.join("") : String(children ?? "");
  return <Comp data-i18n={i18nHash(en)} {...rest}>{t(en, vars)}</Comp>;
}

/* ---- Language picker ---- */
export function LanguagePicker({ style, compact = false }) {
  const { lang } = useLang();
  const [langs, setLangs] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/i18n/languages").then((r) => r.json()).then((d) => { if (alive) setLangs(d.languages || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const list = langs || [{ code: "en", label: "English" }];
  return (
    <select
      aria-label={t("Language")}
      value={lang}
      onChange={(e) => setLanguage(e.target.value)}
      style={style}
      title={t("Language")}
    >
      {list.map((l) => <option key={l.code} value={l.code}>{compact ? l.code.toUpperCase() : l.label}</option>)}
    </select>
  );
}
